import { useState } from 'react';
import {
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  Button, Radio, RadioGroup, Dropdown, Option, Spinner, MessageBar, MessageBarBody
} from '@fluentui/react-components';
import { bootstrapMatchReview } from '../../services/api';
import type { BootstrapPassResponse } from '../../types';

interface Props {
  open: boolean;
  onClose: () => void;
  onCommitted?: () => void;
}

type Mode = 'dryRun' | 'commit';

interface PassLog {
  pass: number;
  processed: number;
  autoMatched: number;
  ambiguous: number;
  noMatch: number;
  total: number;
}

const YEARS = [2024, 2023];
const BATCH_SIZES = [50, 100, 200, 500];

export default function BootstrapDialog({ open, onClose, onCommitted }: Props) {
  const [taxYear, setTaxYear] = useState<number>(2024);
  const [batchSize, setBatchSize] = useState<number>(200);
  const [mode, setMode] = useState<Mode>('dryRun');
  const [cursor, setCursor] = useState<number>(0);
  const [running, setRunning] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<PassLog[]>([]);
  const [done, setDone] = useState<boolean>(false);

  const reset = () => {
    setCursor(0);
    setLogs([]);
    setDone(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const runPass = async () => {
    setRunning(true);
    setError(null);
    try {
      const result: BootstrapPassResponse = await bootstrapMatchReview({
        taxYear, batchSize, cursor, commit: mode === 'commit'
      });
      setLogs(prev => [...prev, {
        pass: prev.length + 1,
        processed: result.processed,
        autoMatched: result.autoMatched,
        ambiguous: result.ambiguous,
        noMatch: result.noMatch,
        total: result.total
      }]);
      if (result.nextCursor == null) {
        setDone(true);
        if (mode === 'commit') onCommitted?.();
      } else {
        setCursor(result.nextCursor);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) handleClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Bootstrap matching</DialogTitle>
          <DialogContent>
            <div style={{ display: 'grid', gap: 12 }}>
              <label>
                Year:&nbsp;
                <Dropdown
                  value={String(taxYear)}
                  selectedOptions={[String(taxYear)]}
                  onOptionSelect={(_, d) => { setTaxYear(Number(d.optionValue)); reset(); }}
                  style={{ minWidth: 100 }}
                >
                  {YEARS.map(y => <Option key={y} value={String(y)}>{String(y)}</Option>)}
                </Dropdown>
              </label>

              <label>
                Batch size:&nbsp;
                <Dropdown
                  value={String(batchSize)}
                  selectedOptions={[String(batchSize)]}
                  onOptionSelect={(_, d) => { setBatchSize(Number(d.optionValue)); reset(); }}
                  style={{ minWidth: 100 }}
                >
                  {BATCH_SIZES.map(n => <Option key={n} value={String(n)}>{String(n)}</Option>)}
                </Dropdown>
              </label>

              <RadioGroup
                value={mode}
                onChange={(_, d) => { setMode(d.value as Mode); reset(); }}
                aria-label="Mode"
              >
                <Radio value="dryRun" label="Dry run (preview, no writes)" />
                <Radio value="commit" label="Commit (write contactIds + seed hash)" />
              </RadioGroup>

              {error && (
                <MessageBar intent="error">
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}

              {logs.length > 0 && (
                <div style={{ borderTop: '1px solid #ccc', paddingTop: 8 }}>
                  <strong>Progress:</strong>
                  <ul style={{ marginTop: 4 }}>
                    {logs.map(l => (
                      <li key={l.pass}>
                        Pass {l.pass}: processed {l.processed} of {l.total} —
                        {' '}{l.autoMatched} auto, {l.ambiguous} ambiguous, {l.noMatch} no-match
                      </li>
                    ))}
                  </ul>
                  {done && <div><strong>Done.</strong> Cursor reached end of data.</div>}
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              {done ? 'Close' : 'Cancel'}
            </Button>
            <Button
              appearance="primary"
              disabled={running || done}
              onClick={runPass}
            >
              {running ? <Spinner size="tiny" /> : (logs.length === 0 ? 'Run pass' : 'Run next pass')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
